import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Client, ClientTask } from '../../context/ClientContext';
import { Avatar, Badge, Button, Icon, PulseDot, SidePanel } from '../ui';
import CreateTaskSidepanel from '../clientDetail/sections/CreateTaskSidepanel';
import { getAttention, relativeDate, STATUS_TONE } from './clientsData';
import useClientTasks from './useClientTasks';
import { formatDate } from '../../utils/dateUtils';

interface ClientDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
}

type DotTone = 'up' | 'warn' | 'info' | 'down' | 'idle';

function taskTone(status: string): DotTone {
  const s = status.toLowerCase();
  if (s.includes('block')) return 'down';
  if (s.includes('complete') || s.includes('done')) return 'idle';
  if (s.includes('submit')) return 'up';
  if (s.includes('progress')) return 'info';
  if (s.includes('schedul')) return 'info';
  return 'idle';
}

function TaskRow({ task }: { task: ClientTask }) {
  const tone = taskTone(task.status);
  const done = tone === 'idle' && task.status.toLowerCase().includes('complete');
  return (
    <div
      className="hover-wash"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        margin: '0 -10px',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <PulseDot tone={tone} pulse={tone === 'up'} title={task.status} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 'var(--text-sm)',
          color: done ? 'var(--text-faint)' : 'var(--text-body)',
          textDecoration: done ? 'line-through' : 'none',
        }}
      >
        {task.title}
      </span>
      <Badge tone={tone}>{task.status}</Badge>
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '9px 0',
        borderBottom: '1px solid var(--border-hairline)',
      }}
    >
      <span className="t-eyebrow">{label}</span>
      <span
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-body)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {children}
      </span>
    </div>
  );
}

interface ClientDetailBodyProps {
  client: Client;
  /** Action row rendered directly under the client identity (name / industry / email). */
  actions?: React.ReactNode;
}

/**
 * Full client detail body — shared by the preview slide-over and the
 * Focus view's live pane.
 */
export function ClientDetailBody({ client, actions }: ClientDetailBodyProps) {
  const att = getAttention(client);
  // Assigned Client Comms board, for the Trello deep link (null = none)
  const [board, setBoard] = useState<{ board_name: string; trello_board_id: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBoard(null); // don't show the previous client's board while loading
    supabase
      .from('comms_boards')
      .select('board_name, trello_board_id')
      .eq('client_id', client.id)
      .eq('is_active', true)
      .limit(1)
      .then(({ data, error }) => {
        if (!cancelled && !error && data && data.length > 0) {
          setBoard(data[0] as { board_name: string; trello_board_id: string });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client.id]);
  const pending = client.tasksDueToday;
  const review = client.tasksAwaitingReview;
  const total = pending + review;
  const tasks = client.tasks || [];

  return (
    <div>
      {/* identity */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: actions ? '14px' : '18px' }}>
        <Avatar name={client.name} size={42} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              className="ck-name"
              style={{
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
                letterSpacing: 'var(--tracking-tight)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {client.name}
            </span>
            {att ? <PulseDot tone={att.tone} title={att.label} /> : null}
          </div>
          {client.industry ? (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: '2px' }}>
              {client.industry}
            </div>
          ) : null}
          {client.email ? (
            <div className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
              {client.email}
            </div>
          ) : null}
        </div>
        <Badge tone={STATUS_TONE[client.status]} dot>{client.status}</Badge>
      </div>

      {/* actions — sit right under the identity so they're reachable without scrolling */}
      {actions ? (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '18px' }}>
          {actions}
        </div>
      ) : null}

      {/* tags */}
      {client.tags.length > 0 ? (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '18px' }}>
          {client.tags.map((tag) => <Badge key={tag} tone="neutral">{tag}</Badge>)}
        </div>
      ) : null}

      {/* mini stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '18px' }}>
        {[
          { label: 'Tasks Today', value: total > 0 ? `${review}/${total}` : '—' },
          { label: 'Due This Week', value: client.tasksDueThisWeek > 0 ? String(client.tasksDueThisWeek) : '—' },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 12px',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '4px' }}>{s.label}</div>
            <div
              className="t-num"
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
              }}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* tasks (present when the full client record is loaded) */}
      {tasks.length > 0 ? (
        <div style={{ marginBottom: '18px' }}>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Tasks</div>
          {tasks.slice(0, 4).map((task) => <TaskRow key={task.id} task={task} />)}
          {tasks.length > 4 ? (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', padding: '6px 0 0', display: 'block' }}>
              +{tasks.length - 4} more
            </span>
          ) : null}
        </div>
      ) : null}

      {/* notes (present when the full client record is loaded) */}
      {client.notes ? (
        <div style={{ marginBottom: '18px' }}>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Notes</div>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 'var(--leading-normal)' }}>
            {client.notes}
          </p>
        </div>
      ) : null}

      {/* meta */}
      <div style={{ borderTop: '1px solid var(--border-hairline)' }}>
        <MetaRow label="Owner">
          {client.accountOwner ? (
            <>
              <Avatar name={client.accountOwner.name} size={20} /> {client.accountOwner.name}
            </>
          ) : (
            <span style={{ color: 'var(--text-faint)' }}>Unassigned</span>
          )}
        </MetaRow>
        <MetaRow label="Client Since">
          <span className="t-num">{client.startDate ? formatDate(client.startDate, 'MMM d, yyyy') : '—'}</span>
        </MetaRow>
        <MetaRow label="Last Activity">
          <span className="t-num">{relativeDate(client.lastActivityDate)}</span>
        </MetaRow>
        {board ? (
          <MetaRow label="Trello">
            <a
              href={`https://trello.com/b/${board.trello_board_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="pressable"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--accent-text)',
                textDecoration: 'none',
                fontSize: 'var(--text-sm)',
                maxWidth: '220px',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {board.board_name}
              </span>
              <Icon name="external-link" size={12} style={{ flexShrink: 0 }} />
            </a>
          </MetaRow>
        ) : null}
        <MetaRow label="Next Meeting">
          <span className="t-num">
            {client.nextMeetingDate ? formatDate(client.nextMeetingDate, 'MMM d, h:mm a') : '—'}
          </span>
        </MetaRow>
      </div>
    </div>
  );
}

/**
 * Client preview slide-over — quick look from the table, with the full
 * client page one click away.
 */
export default function ClientDetailPanel({ isOpen, onClose, client }: ClientDetailPanelProps) {
  const navigate = useNavigate();
  const [showAddTask, setShowAddTask] = useState(false);
  // roster rows only carry task counts — fetch the list for the preview
  const tasks = useClientTasks(client?.id, isOpen);

  const openClient = () => {
    if (!client) return;
    onClose();
    navigate(`/clients/${client.id}`);
  };

  const addTask = () => {
    if (!client) return;
    setShowAddTask(true);
  };

  return (
    <>
    <SidePanel
      open={isOpen && !!client}
      onClose={onClose}
      eyebrow="Client"
      title={client?.name ?? ''}
      footer={
        <>
          <Button icon="plus" onClick={addTask}>Add Task</Button>
          <Button variant="secondary" iconTrailing="arrow-right" nudge onClick={openClient}>Open Client</Button>
          <Button variant="ghost" icon="pencil" onClick={openClient}>Edit</Button>
        </>
      }
    >
      {client ? <ClientDetailBody client={tasks.length > 0 ? { ...client, tasks } : client} /> : null}
    </SidePanel>

      {client && (
        <CreateTaskSidepanel
          isOpen={showAddTask}
          onClose={() => setShowAddTask(false)}
          client={client}
        />
      )}
    </>
  );
}
